import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Upload, UploadFile } from 'antd';
import { Controller, useWatch } from 'react-hook-form';

import { ReactComponent as IconUpload } from 'app/assets/img/upload-icon.svg';

import ModalContainer from 'app/components/ModalContainer';
import useTitleSize from 'app/components/StyledTable/useTitleSize';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import StyledTable from 'app/components/StyledTable';
import StyledButton from 'app/components/StyledButton';
import { formatNumber, formatTimeFrame } from 'utils/helper';
import NumberInput from 'app/components/NumberInput';
import TooltipParagraph from 'app/pages/Catalog/tooltipParagraph';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { adjustOrder } from 'app/services/orderApi';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { useHistory } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { CheckoutDetail } from '../slice/types';

const { Dragger } = Upload;

export enum EDIT_FLOW_TYPES {
  PROXY_BUYING,
  NORMAL_BUYING,
}

export enum TICKET_TYPES {
  FOC = 'FOC',
  NORMAL_GIFT = 'NORMAL_GIFT',
  MAIN = 'MAIN',
  ENTRANCE_TICKET = 'ENTRANCE_TICKET',
}

export type EDIT_FORM_VALUES = {
  giftTickets: {
    _productId: number;
    amount: number | null;
    unitPrice: number;
    maxQuantityFOC?: number;
    _original: {
      amount: number;
      unitPrice: number;
    };
    type: TICKET_TYPES.FOC | TICKET_TYPES.NORMAL_GIFT;
  }[];
  mainTickets: {
    _productId: number;
    amount: number;
    unitPrice: number | null;
    _original: {
      amount: number;
      unitPrice: number;
    };
    type: TICKET_TYPES.MAIN | TICKET_TYPES.ENTRANCE_TICKET;
  }[];
  docs?: UploadFile<any> | undefined;
};

export enum ADJUST_ORDER_ERROR_CODE {
  ORDER_CANCELED = 'MSG070',
  NOT_ENOUGH_INVENTORY = 'MSG050',
  MAX_QUANTITY_FOC = 'MSG0710',
  NO_EDIT_ENTRANCE_TICKET = 'MSG072201',
}

export enum ADJUST_ORDER_STATE {
  PAYMENT_PENDING = 'paymentPending',
  PENDING_APPROVE = 'pendingApprove',
  INIT = 'init',
}

const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'message/rfc822', // .eml
];

const MAX_FILE_SIZE = 5; // 5MB

const MAX_PRICE = 999999999;

interface EditOrderPopupProps {
  open: boolean;
  onClose: () => void;
  groupTicketData: Record<string, any>[];
  idHash: string;
  getOrderDetail: () => Promise<CheckoutDetail>;
  buyingFlow: EDIT_FLOW_TYPES;
}

const EditOrderPopup: FC<EditOrderPopupProps> = props => {
  const { open, onClose, idHash, getOrderDetail, groupTicketData, buyingFlow } =
    props;
  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    reset,
    clearErrors,
    getValues,
    setError,
    setValue,
  } = useFormContext<EDIT_FORM_VALUES>();
  const translations = useSelector(selectTranslations);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isProxyBuyingFlow = buyingFlow === EDIT_FLOW_TYPES.PROXY_BUYING;

  const [giftTicketsData, mainTicketsData] = useMemo(() => {
    const mainTicketsData: Record<string, any> = [];
    const giftTicketsData: Record<string, any> = [];

    groupTicketData?.forEach(group => {
      const items = Object.values(group?.catalogs).flatMap(
        catalog => (catalog as Record<string, any>)?.items,
      );

      items.forEach(item => {
        if (
          [TICKET_TYPES.NORMAL_GIFT, TICKET_TYPES.FOC].includes(
            item?.higherLevelItem,
          )
        )
          giftTicketsData.push(item);
        else mainTicketsData.push(item);
      });
    });

    return [giftTicketsData, mainTicketsData];
  }, [groupTicketData]);

  const dirtyGiftTickets = dirtyFields?.giftTickets;
  const dirtyMainTickets = dirtyFields?.mainTickets;
  const dirtyDocs = dirtyFields?.docs;
  const isDirtyData =
    !!dirtyGiftTickets?.length || !!dirtyMainTickets?.length || !!dirtyDocs;

  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  const { fields: fieldsGiftTicket } = useFieldArray({
    control,
    name: 'giftTickets',
  });

  const { fields: fieldsMainTicket } = useFieldArray({
    control,
    name: 'mainTickets',
  });

  const mainTicketsWatched = useWatch({
    control,
    name: 'mainTickets',
  });

  const titleColumnSizes = useTitleSize({
    title: [
      'b2b.txt.ticketName',
      'b2b.txt.ticketPark',
      'b2b.txt.ticketType',
      'b2b.txt.usageDate',
      'b2b.txt.ticket-detail',
      'b2b.table.quantity',
      'b2b.txt.unitPrice',
      'b2b.txt.total-amount',
    ],
  });

  const columns = useMemo(
    () => [
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.ticketName']}
          </div>
        ),
        dataIndex: 'product',
        key: 'productName',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              width: Math.max(180, titleColumnSizes['b2b.txt.ticketName']),
              paddingRight: 10,
            }}
          >
            <TooltipParagraph
              ellipsis={{ rows: 2 }}
              style={{ marginBottom: 0 }}
            >
              {value}
            </TooltipParagraph>
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.ticketPark']}
          </div>
        ),
        dataIndex: 'park',
        key: 'park',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              width: Math.max(160, titleColumnSizes['b2b.txt.ticketPark']),
            }}
          >
            <TooltipParagraph
              ellipsis={{ rows: 2 }}
              style={{ marginBottom: 0 }}
            >
              {value}
            </TooltipParagraph>
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.table.quantity']}
          </div>
        ),
        dataIndex: 'ticketQuantity',
        key: 'ticketQuantity',
        render: (value, _record, index) => {
          return (
            <div
              style={{
                textAlign: 'center',
                width: Math.max(100, titleColumnSizes['b2b.table.quantity']),
              }}
            >
              <QuantityCell index={index} ticketType={value?._ticketType} />
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.unitPrice'] ?? ''}(đ)
          </div>
        ),
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        render: (value, _record, index) => {
          return (
            <div
              style={{
                textAlign: 'right',
                width: Math.max(110, titleColumnSizes['b2b.txt.unitPrice']),
              }}
            >
              <UnitPriceCell
                index={index}
                ticketType={value?._ticketType}
                originalUnitPrice={value?.originalPrice}
              />
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.total-amount'] ?? ''}(đ)
          </div>
        ),
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        render: (value, _record, index) => {
          const getChild = () => {
            switch (value?._ticketType) {
              case TICKET_TYPES.MAIN:
                return <TotalPriceMainTicketCell index={index} />;

              case TICKET_TYPES.FOC:
              case TICKET_TYPES.NORMAL_GIFT:
              case TICKET_TYPES.ENTRANCE_TICKET:
              default:
                return (
                  <>
                    {formatNumber(
                      value?.amount * value?.originalPrice,
                    )?.replaceAll(',', '.')}
                    đ
                  </>
                );
            }
          };

          return (
            <div
              style={{
                textAlign: 'right',
                minWidth: Math.max(
                  118,
                  titleColumnSizes['b2b.txt.total-amount'],
                ),
              }}
            >
              {getChild()}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.ticketType']}
          </div>
        ),
        dataIndex: 'ticketType',
        key: 'ticketType',
        render: value => (
          <div
            style={{
              textAlign: 'center',
              width: Math.max(120, titleColumnSizes['b2b.txt.ticketType']),
            }}
          >
            <TooltipParagraph
              ellipsis={{ rows: 2 }}
              style={{ marginBottom: 0 }}
            >
              {value}
            </TooltipParagraph>
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.usageDate']}
          </div>
        ),
        dataIndex: 'usageDate',
        key: 'usageDate',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              width: Math.max(160, titleColumnSizes['b2b.txt.usageDate']),
            }}
          >
            {!!value
              ? `${translations?.['b2c.txt.use-from'] ?? ''} ${value}`
              : ''}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.ticket-detail']}
          </div>
        ),
        dataIndex: 'ticketDetail',
        key: 'ticketDetail',
        render: value => {
          const zone = value?.zone?.name;
          const restaurant = value?.restaurant?.name;
          const performance = formatTimeFrame(
            value?.product?.usedArea2Object?.label,
          );

          return (
            <div
              style={{
                textAlign: 'left',
                width: Math.max(128, titleColumnSizes['b2b.txt.ticket-detail']),
              }}
            >
              {zone && <div>{zone}</div>}
              {restaurant && <div>{restaurant}</div>}
              {performance && <div>{performance}</div>}
            </div>
          );
        },
      },
    ],
    [titleColumnSizes, translations],
  );

  const giftTicketDataSource = useMemo(() => {
    return (fieldsGiftTicket as Record<string, any>)
      .map(field => {
        const ticket =
          giftTicketsData?.find(ticket => ticket?.id === field?._productId) ??
          {};

        if (!isProxyBuyingFlow && ticket?.higherLevelItem !== 'FOC') return {};

        return {
          product: ticket.show?.name,
          park: ticket?.park?.name,
          ticketQuantity: {
            ...ticket,
            _ticketType:
              ticket?.higherLevelItem === 'FOC'
                ? TICKET_TYPES.FOC
                : TICKET_TYPES.NORMAL_GIFT,
          },
          unitPrice: {
            ...ticket,
            _ticketType:
              ticket?.higherLevelItem === 'FOC'
                ? TICKET_TYPES.FOC
                : TICKET_TYPES.NORMAL_GIFT,
          },
          totalAmount: {
            ...ticket,
            _ticketType:
              ticket?.higherLevelItem === 'FOC'
                ? TICKET_TYPES.FOC
                : TICKET_TYPES.NORMAL_GIFT,
          },
          ticketType: ticket?.catalog?.name,
          usageDate: ticket.date,
          ticketDetail: ticket,
        };
      })
      .filter(ob => Object.keys(ob).length > 0);
  }, [fieldsGiftTicket, giftTicketsData, isProxyBuyingFlow]);

  const mainTicketsDataSource = useMemo(() => {
    return (fieldsMainTicket as Record<string, any>).map(field => {
      const ticket =
        mainTicketsData?.find(ticket => ticket?.id === field?._productId) ?? {};

      return {
        product: ticket.show?.name,
        park: ticket?.park?.name,
        ticketQuantity: {
          ...ticket,
          _ticketType: !!ticket?.show?.isB2BTicketAddon
            ? TICKET_TYPES.ENTRANCE_TICKET
            : TICKET_TYPES.MAIN,
        },
        unitPrice: {
          ...ticket,
          _ticketType: !!ticket?.show?.isB2BTicketAddon
            ? TICKET_TYPES.ENTRANCE_TICKET
            : TICKET_TYPES.MAIN,
        },
        totalAmount: {
          ...ticket,
          _ticketType: !!ticket?.show?.isB2BTicketAddon
            ? TICKET_TYPES.ENTRANCE_TICKET
            : TICKET_TYPES.MAIN,
        },
        ticketType: ticket?.catalog?.name,
        usageDate: ticket.date,
        ticketDetail: ticket,
      };
    });
  }, [fieldsMainTicket, mainTicketsData]);

  const totalAmount = useMemo(() => {
    return mainTicketsWatched?.reduce(
      (result, ticket) => result + ticket?.amount * (ticket?.unitPrice ?? 0),
      0,
    );
  }, [mainTicketsWatched]);

  const handleUpdateOrder = data => {
    const giftNormalTicketData = data?.giftTickets?.filter(
      i => i?.type !== TICKET_TYPES.FOC, // NORMAL_GIFT
    );

    const focTicketData = data?.giftTickets?.filter(
      i => i?.type === TICKET_TYPES.FOC,
    );

    // check FOC
    const isNeedConfirmFOC = focTicketData?.some(
      ticket => ticket?.amount > ticket?._original?.amount,
    );

    // check Main Ticket
    const isNeedConfirmMainTicket = data?.mainTickets?.some(
      ticket => ticket?.unitPrice !== ticket?._original?.unitPrice,
    );

    const onUpdateOrder = async () => {
      try {
        setIsLoading(true);
        if (!executeRecaptcha) return;
        const tokenCaptcha = await executeRecaptcha('adjustOrder');
        const formData = new FormData();
        formData.append('idHash', idHash);
        formData.append('captcha', tokenCaptcha);
        giftNormalTicketData?.forEach((gift, index) => {
          formData.append(`gift[${index}][item_id]`, gift?._productId);
          formData.append(`gift[${index}][quantity]`, gift?.amount);
          formData.append(`gift[${index}][adjust_price]`, gift?.unitPrice);
        });
        focTicketData?.forEach((foc, index) => {
          formData.append(`foc[${index}][item_id]`, foc?._productId);
          formData.append(`foc[${index}][quantity]`, foc?.amount);
        });
        data?.mainTickets?.forEach((mainTicket, index) => {
          formData.append(`items[${index}][item_id]`, mainTicket?._productId);
          formData.append(
            `items[${index}][adjust_price]`,
            mainTicket?.unitPrice,
          );
        });

        const adjustAttachFile = data?.docs;

        if (!!adjustAttachFile)
          formData.append('adjustAttach', adjustAttachFile);

        await adjustOrder(formData);
        onClose();
        getOrderDetail();
      } catch (error: any) {
        if (error?.data?.code === ADJUST_ORDER_ERROR_CODE.ORDER_CANCELED) {
          const message = error?.message ?? '';

          onOpenDialogNoti({
            type: 'ERROR',
            isShow: true,
            title: message,
            content: '',
            onCloseDialog: () => {
              onCloseDialogNoti();
              history.replace('/account?route=tab-3');
            },
            maskClosable: false,
          });

          return;
        }

        const isInventoryError =
          error?.data?.code === ADJUST_ORDER_ERROR_CODE.NOT_ENOUGH_INVENTORY;

        if (isInventoryError) {
          const dataError = error?.data?.dataInventory ?? [];

          if (Array.isArray(dataError)) {
            const giftTicketFormData = getValues()?.giftTickets;

            dataError?.forEach((item, idx) => {
              const itemId = item?.item_id;
              const indexOfItemIdInsideFOC = giftTicketFormData.findIndex(
                i => +i?._productId === +itemId,
              );

              if (indexOfItemIdInsideFOC > -1) {
                setError(
                  `giftTickets.${indexOfItemIdInsideFOC}.amount`,
                  {
                    type: 'max_inventory',
                    message: item?.message ?? error?.message,
                  },
                  { shouldFocus: idx === 0 },
                );
              }
            });
          }

          return;
        }

        const isMaxQuantityFocError =
          error?.data?.code === ADJUST_ORDER_ERROR_CODE.MAX_QUANTITY_FOC;

        if (isMaxQuantityFocError) {
          const maxQuantityFOC = error?.data?.maxQuantityFoc;
          const giftTicketFormData = getValues()?.giftTickets;

          const indexOfErrorItemIdInsideFOC = giftTicketFormData.findIndex(
            i => +i?._productId === +error?.data?.item_id,
          );

          if (indexOfErrorItemIdInsideFOC > -1) {
            setValue(
              `giftTickets.${indexOfErrorItemIdInsideFOC}.maxQuantityFOC`,
              maxQuantityFOC,
            );

            setError(
              `giftTickets.${indexOfErrorItemIdInsideFOC}.amount`,
              {
                type: 'max_inventory',
                message: error?.message,
              },
              { shouldFocus: true },
            );
          }

          return;
        }

        const isNoEditEntranceTicket =
          error?.data?.code === ADJUST_ORDER_ERROR_CODE.NO_EDIT_ENTRANCE_TICKET;

        if (isNoEditEntranceTicket) {
          const mainTicketFormData = getValues()?.mainTickets;

          const indexOfErrorItemIdInsideMainTickets =
            mainTicketFormData.findIndex(
              i => +i?._productId === +error?.data?.item_id,
            );

          if (indexOfErrorItemIdInsideMainTickets > -1) {
            setError(
              `mainTickets.${indexOfErrorItemIdInsideMainTickets}.unitPrice`,
              {
                type: 'no_edit_entrance_ticket',
                message: error?.message,
              },
              { shouldFocus: true },
            );
          }

          return;
        }

        const message = error?.response?.data?.message ?? error?.message ?? '';
        onOpenDialogNoti({
          type: 'ERROR',
          isShow: true,
          title: message,
          content: '',
          onCloseDialog: () => {
            onCloseDialogNoti();
          },
          maskClosable: false,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isNeedConfirmFOC || isNeedConfirmMainTicket) {
      return onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        content: translations?.['b2b.txt.confirmOrderChanges'] ?? '',
        title: '',
        closable: false,
        buttons: [
          {
            text: translations?.['b2c.txt.close'] ?? '',
            bg: '#ffff',
            border: 'solid 1px #808080',
            color: '#808080',
            onClick: () => {
              onCloseDialogNoti();
            },
          },
          {
            text: translations?.['b2b.btn.accept'] ?? '',
            bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
            color: 'white',
            onClick: () => {
              onUpdateOrder();
              onCloseDialogNoti();
            },
          },
        ],
      });
    }

    onUpdateOrder();
  };

  useEffect(() => {
    const setFormDefaultValues = () => {
      if (!giftTicketsData?.length && !mainTicketsData?.length) return;

      reset({
        giftTickets: giftTicketsData.map(ticket => {
          const maxQuantityFOC = isProxyBuyingFlow
            ? ticket?.maxQuantityFoc
            : ticket?.amount;

          return {
            _productId: ticket.id,
            amount: +ticket.amount,
            unitPrice: +ticket.originalPrice,
            ...(ticket?.higherLevelItem === TICKET_TYPES.FOC
              ? {
                  maxQuantityFOC:
                    maxQuantityFOC === null ||
                    maxQuantityFOC === undefined ||
                    isNaN(maxQuantityFOC)
                      ? 1e3
                      : +maxQuantityFOC,
                  type: TICKET_TYPES.FOC,
                }
              : { type: TICKET_TYPES.NORMAL_GIFT }),
            _original: {
              amount: +ticket.amount,
              unitPrice: +ticket.originalPrice,
            },
          };
        }),

        mainTickets: mainTicketsData.map(ticket => ({
          _productId: ticket.id,
          amount: +ticket.amount,
          unitPrice: +ticket.originalPrice,
          _original: {
            amount: +ticket.amount,
            unitPrice: +ticket.originalPrice,
          },
          type: !!ticket?.show?.isB2BTicketAddon
            ? TICKET_TYPES.ENTRANCE_TICKET
            : TICKET_TYPES.MAIN,
        })),
      });
    };

    setFormDefaultValues();
  }, [giftTicketsData, mainTicketsData, reset, isProxyBuyingFlow]);

  return (
    <ModalContainer
      isShow={open}
      onCloseDialog={() => {
        if (!isLoading) {
          clearErrors();
          reset();
          onClose();
        }
      }}
      maskClosable={false}
      modalPadding="16px 24px 32px"
      modalHeight="initial"
      styleSlots={{
        iconWrapperStyle: {
          margin: '0 16px 0 0',
        },
        modalContentStyle: {
          maxWidth: 980,
          width: '96vw',
          maxHeight: '96vh',
        },
        modalBodyStyle: {
          height: 'fit-content',
        },
      }}
      modalProps={{ destroyOnClose: true }}
    >
      <LoadingContainer loading={isLoading}>
        <Title>{translations?.['b2b.editOrder.editInfo'] ?? ''}</Title>
        {giftTicketDataSource?.length > 0 && (
          <div>
            <SubTitle>
              {translations?.['b2b.upgrade.order-gift'] ?? ''}
            </SubTitle>
            <StyledTable
              columns={columns}
              dataSource={giftTicketDataSource}
              tableProps={{
                pagination: false,
                scroll: {
                  x: giftTicketDataSource?.length ? 'auto' : 'max-content',
                  y: giftTicketDataSource?.length
                    ? buyingFlow === EDIT_FLOW_TYPES.PROXY_BUYING
                      ? '180px'
                      : '50vh'
                    : undefined,
                },
              }}
              styleSlots={{
                antTableContainer: {
                  overflow: 'hidden',
                },
                antTableHeader: {
                  border: 'none !important',
                  borderRadius: '0 !important',
                },
                tableCSS: {
                  borderTopLeftRadius: '0 !important',
                  borderTopRightRadius: '0 !important',
                },
                antTableBody: {
                  paddingBottom: 10,
                },
                td: {
                  padding: '20px 10px',
                },
              }}
            />
          </div>
        )}
        {mainTicketsDataSource?.length > 0 && isProxyBuyingFlow && (
          <div>
            <SubTitle>
              {translations?.['b2b.editOrder.ticketList'] ?? ''}
            </SubTitle>
            <StyledTable
              columns={columns}
              dataSource={mainTicketsDataSource}
              tableProps={{
                pagination: false,
                scroll: {
                  x: mainTicketsDataSource?.length ? 'auto' : 'max-content',
                  y: mainTicketsDataSource?.length ? '180px' : undefined,
                },
              }}
              styleSlots={{
                antTableContainer: {
                  overflow: 'hidden',
                },
                antTableHeader: {
                  border: 'none !important',
                  borderRadius: '0 !important',
                },
                tableCSS: {
                  borderTopLeftRadius: '0 !important',
                  borderTopRightRadius: '0 !important',
                },
                antTableBody: {
                  paddingBottom: 10,
                },
                td: {
                  padding: '20px 10px',
                },
              }}
            />
          </div>
        )}

        {isProxyBuyingFlow && (
          <div>
            <SubTitle>
              {translations?.['b2b.txt.uploadTheSubmission'] ?? ''}
            </SubTitle>
            <div>
              <Title
                style={{ fontSize: 14, fontWeight: 500, textAlign: 'left' }}
              >
                {translations?.['b2b.text.uploadFile'] ?? ''}
              </Title>
              <Controller
                name="docs"
                control={control}
                render={({ field }) => {
                  return (
                    <StyledDraggerContainer>
                      <StyledDragger
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.eml"
                        name="file"
                        multiple={false}
                        maxCount={1}
                        beforeUpload={file => {
                          if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
                            onOpenDialogNoti({
                              type: 'ERROR',
                              title:
                                translations?.[
                                  'b2b.txt.uploadApprovalDocumentType'
                                ] ?? '',
                              content: '',
                              isShow: true,
                              buttons: [],
                              maskClosable: false,
                              closable: true,
                              onCloseDialog: onCloseDialogNoti,
                            });
                            return Upload.LIST_IGNORE;
                          }

                          if (file.size / 1024 / 1024 > MAX_FILE_SIZE) {
                            onOpenDialogNoti({
                              type: 'ERROR',
                              title:
                                translations?.[
                                  'b2b.txt.uploadApprovalDocumentSize'
                                ] ?? '',
                              content: '',
                              isShow: true,
                              buttons: [],
                              maskClosable: false,
                              closable: true,
                              onCloseDialog: onCloseDialogNoti,
                            });
                            return Upload.LIST_IGNORE;
                          }

                          field.onChange(file);
                          return false;
                        }}
                        onRemove={() => {
                          field.onChange(undefined);
                        }}
                        fileList={field.value ? [field.value] : []}
                        style={{
                          marginTop: 16,
                          border: 'dashed 2px #44a2ff',
                          borderRadius: 10,
                        }}
                      >
                        <div className="d-flex justify-content-center flex-column align-items-center">
                          <IconUpload />
                          <span
                            dangerouslySetInnerHTML={{
                              __html:
                                translations?.[
                                  'b2b.txt.dragDropFileToUpload'
                                ] ?? '',
                            }}
                            style={{ margin: '8px 0px', fontSize: 14 }}
                          />
                          <Note>
                            {translations?.['b2b.editOrder.fileUploadType'] ??
                              ''}
                          </Note>
                          <Note>
                            {translations?.['b2b.editOrder.fileUploadRule'] ??
                              ''}
                          </Note>
                        </div>
                      </StyledDragger>
                    </StyledDraggerContainer>
                  );
                }}
              />
            </div>
          </div>
        )}

        <div
          className="d-flex align-items-center justify-content-end"
          style={{ marginTop: 28 }}
        >
          {isProxyBuyingFlow && (
            <div>
              <p
                style={{
                  margin: 0,
                  fontWeight: 500,
                  fontSize: 12,
                  color: '#57575C',
                  textAlign: 'right',
                }}
              >
                {translations?.['b2b.txt.total-amount'] ?? ''}
              </p>
              <h5 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>
                ₫{formatNumber(totalAmount).replaceAll(',', '.')}
              </h5>
            </div>
          )}
          <StyledButton
            textColor="#ffffff"
            style={{ marginLeft: 24 }}
            onClick={handleSubmit(handleUpdateOrder)}
            disabled={!isDirtyData}
          >
            {translations?.['b2c.btn.update'] ?? ''}
          </StyledButton>
        </div>
      </LoadingContainer>
    </ModalContainer>
  );
};

export default EditOrderPopup;

const QuantityCell = (props: { index: number; ticketType: TICKET_TYPES }) => {
  const { index, ticketType } = props;
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  return ticketType === TICKET_TYPES.FOC ? (
    <Controller
      control={control}
      name={`giftTickets.${index}.amount`}
      render={({ field }) => (
        <NumberInput
          styleSlot={{
            errMsg: {
              width: 'max-content',
              position: 'absolute',
              left: 0,
              top: '100%',
              zIndex: 2,
            },
          }}
          {...field}
          errMsg={errors?.giftTickets?.[index]?.amount?.message}
          autoComplete="off"
        />
      )}
    />
  ) : (
    <>
      {getValues(
        ticketType === TICKET_TYPES.NORMAL_GIFT
          ? `giftTickets.${index}.amount`
          : `mainTickets.${index}.amount`,
      )}
    </>
  );
};

const UnitPriceCell = (props: {
  index: number;
  ticketType: TICKET_TYPES;
  originalUnitPrice: number;
}) => {
  const { index, ticketType, originalUnitPrice } = props;
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  return ticketType === TICKET_TYPES.MAIN ||
    ticketType === TICKET_TYPES.ENTRANCE_TICKET ? (
    <Controller
      control={control}
      name={`mainTickets.${index}.unitPrice`}
      render={({ field }) => {
        const disabled =
          ticketType === TICKET_TYPES.ENTRANCE_TICKET || !originalUnitPrice;

        return (
          <NumberInput
            disabled={disabled}
            max={MAX_PRICE}
            actionButton={false}
            thousandSeparator="."
            suffix="đ"
            styleSlot={{
              errMsg: {
                width: 'max-content',
                position: 'absolute',
                left: 0,
                top: '100%',
                zIndex: 2,
              },
              inputContainer: disabled
                ? { border: 'none', background: 'none' }
                : {},
            }}
            style={
              disabled
                ? { background: 'none', textAlign: 'right', padding: 0 }
                : {}
            }
            {...field}
            errMsg={errors?.mainTickets?.[index]?.unitPrice?.message}
            autoComplete="off"
          />
        );
      }}
    />
  ) : (
    <>
      {formatNumber(getValues(`giftTickets.${index}.unitPrice`)).replaceAll(
        ',',
        '.',
      ) + 'đ'}
    </>
  );
};

const TotalPriceMainTicketCell = (props: { index: number }) => {
  const { index } = props;
  const { watch } = useFormContext();

  const unitPrice = watch(`mainTickets.${index}.unitPrice`);
  const amount = watch(`mainTickets.${index}.amount`);

  return <>{formatNumber(unitPrice * amount).replaceAll(',', '.')}đ</>;
};

const Title = styled.h3`
  text-align: center;
  font-weight: 600;
  font-size: 24px;
  margin: 0;
`;

const SubTitle = styled(Title)`
  font-size: 16px;
  padding: 16px 0px;
  text-align: left;
  margin-top: 16px;
`;

const Note = styled.span`
  font-size: 12px;
  color: #71717a;
`;

const StyledDragger = styled(Dragger)`
  &.ant-upload.ant-upload-drag .ant-upload {
    padding: 10px;
  }
`;

const StyledDraggerContainer = styled.div`
  .ant-upload-list-item-name {
    font-weight: bold;
    color: #080000;
  }

  .ant-upload-text-icon {
    display: flex;
    .anticon-paper-clip {
      svg {
        fill: #000000;
        transform: scale(1.2);
      }
    }
  }
`;
